import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { readAdminSettings, saveAdminSettings } from '../demo/store.js';
import PersonPicker from './PersonPicker.jsx';
import {
  WEEKDAYS, addHoliday, calendarDirty, holidayLabel, hourLabel, removeHoliday, toggleDay, wordsForLoadError, wordsForSettingsError,
} from '../lib/adminSettings.js';

/* The Admin screen's "Ticket rules" tab: the first REAL settings on that screen.
 *
 * The work hours, the reopen window, how long a closed ticket keeps its files, and the desk email
 * (the company domains, which priorities alert, each desk's addresses) are saved on the server (GET/PUT /api/ticket-settings). Only an
 * IT Admin can read or save them. Two admins editing at once: the second save is refused ("another
 * admin changed these settings first, so your change was NOT applied") and the form reloads. The other
 * Admin tabs are still a preview. */

// The server refuses a day that starts or ends at these hours (daylight saving), so the pickers leave them out.
const UNRELIABLE_HOURS = [2];
const START_HOURS = Array.from({ length: 23 }, (_, h) => h).filter((h) => !UNRELIABLE_HOURS.includes(h));
const END_HOURS = Array.from({ length: 23 }, (_, i) => i + 1).filter((h) => !UNRELIABLE_HOURS.includes(h));

function lastChanged(part) {
  if (!part.updatedBy) return 'Never changed here - these are the built-in starting values.';
  const when = part.updatedAt ? new Date(part.updatedAt).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }) : '';
  return `Last changed by ${part.updatedBy}${when ? ` on ${when}` : ''}.`;
}

export default function TicketRules() {
  // status: 'loading' | 'ok' | 'error'
  const [state, setState] = useState({ status: 'loading' });
  // What the last save said, per section. It lives HERE, above the forms, because a save changes the version
  // and the forms are rebuilt from the server's values: a message kept inside a form would be thrown away.
  const [notices, setNotices] = useState({});
  const setNotice = (section, notice) => setNotices((n) => ({ ...n, [section]: notice }));

  // The settings on screen NOW. A save's answer is compared with this, not with the values captured when its
  // button was pressed: two quick saves from this tab must not blame "another admin" for the first one.
  const shown = useRef(null);
  // Every new set of settings comes through here: a save's answer, a reload after "someone saved first", the
  // Reload button. Each section keeps whichever version is newer: answers can arrive out of order, and an older
  // one must not put an old value back on screen. If a section other than `own` (the one that just saved or
  // asked for the reload) moved to a newer version, another admin changed it and its form is rebuilt from
  // their values: say so, or an unsaved edit there vanishes without a word. A red message already on that
  // section ("your change was NOT applied") is stronger, so it stays.
  const adopt = useCallback((next, own) => {
    const prev = shown.current;
    const merged = { ...next };
    if (prev) {
      for (const [section, field] of [
        ['calendar', 'calendar'], ['reopen', 'reopenWindow'], ['fileKeep', 'fileKeep'],
        ['emailDomains', 'emailDomains'], ['alertPriorities', 'alertPriorities'],
      ]) {
        // The file keep time (019) and the desk email settings (020) are null until applied: nothing to compare.
        if (!next[field] || !prev[field]) continue;
        if (next[field].version < prev[field].version) merged[field] = prev[field];
        else if (section !== own && next[field].version > prev[field].version) {
          setNotices((n) => (n[section] && n[section].kind === 'error' ? n : {
            ...n,
            [section]: { kind: 'info', text: 'Another admin changed this section, so it now shows their values. Any change you had not saved here was cleared.' },
          }));
        }
      }
      // The desk email: one version per desk, so each desk is its own section ('deskEmail:<desk>').
      if (Array.isArray(next.deskEmail) && Array.isArray(prev.deskEmail)) {
        merged.deskEmail = next.deskEmail.map((d) => {
          const old = prev.deskEmail.find((p) => p.desk === d.desk);
          if (!old) return d;
          const section = `deskEmail:${d.desk}`;
          if (d.version < old.version) return old;
          if (section !== own && d.version > old.version) {
            setNotices((n) => (n[section] && n[section].kind === 'error' ? n : {
              ...n,
              [section]: { kind: 'info', text: 'Another admin changed this desk, so it now shows their addresses. Any change you had not saved here was cleared.' },
            }));
          }
          return d;
        });
      }
    }
    shown.current = merged;
    setState({ status: 'ok', settings: merged });
  }, []);

  const load = useCallback(async (own) => {
    try {
      adopt(await readAdminSettings(), own);
    } catch (err) {
      setState({ status: 'error', message: wordsForLoadError(err) });
    }
  }, [adopt]);
  useEffect(() => { load(); }, [load]);

  if (state.status === 'loading') return <p className="muted">Loading…</p>;
  if (state.status === 'error') {
    return (
      <div className="banner banner--bad" role="alert">
        <span className="material-symbols-outlined" aria-hidden="true">error</span>
        <span>{state.message}</span>
        <button type="button" className="btn btn--quiet" onClick={() => { setState({ status: 'loading' }); load(); }}>Try again</button>
      </div>
    );
  }

  const { settings } = state;
  // A save that landed but could not be read back comes with no settings: read them again.
  const applySaved = (section) => (next) => (next ? adopt(next, section) : load(section));
  return (
    <>
      <h2 className="adh">Work hours</h2>
      <p className="adp">
        The hours and days the desk counts when it works out a deadline. Time outside them, and the
        holidays below, never count against IT. Hours are in Mountain Time ({settings.timeZone}); that
        is fixed. These are saved for real, but nothing uses them yet: new deadlines will count with
        them once ticket saving is switched on, and the deadline meters on screen still use the
        built-in hours for now. After that, a change can take up to 15 minutes to reach every deadline.
      </p>
      <WorkHoursForm
        key={`cal-${settings.calendar.version}`}
        saved={settings.calendar}
        notice={notices.calendar}
        setNotice={(n) => setNotice('calendar', n)}
        onSaved={applySaved('calendar')}
        onReload={() => load('calendar')}
      />

      <h2 className="adh" style={{ marginTop: 28 }}>Reopening</h2>
      <p className="adp">
        How many days after a ticket is resolved it can still be reopened. Setting this changes the
        number only; reopening itself arrives with the next update.
      </p>
      <DaysForm
        key={`reopen-${settings.reopenWindow.version}`}
        section="reopen"
        label="Days a resolved ticket can be reopened"
        max={90}
        button="Save reopen window"
        saved={settings.reopenWindow}
        notice={notices.reopen}
        setNotice={(n) => setNotice('reopen', n)}
        onSaved={applySaved('reopen')}
        onReload={() => load('reopen')}
      />

      <h2 className="adh" style={{ marginTop: 28 }}>Files on closed tickets</h2>
      <p className="adp">
        How many days after a ticket closes its screenshots and files are deleted. A ticket closes when
        its reopen time above runs out. The ticket and its messages stay; only the files go. The
        clean-up runs once a night.
      </p>
      {settings.fileKeep ? (
        <DaysForm
          key={`keep-${settings.fileKeep.version}`}
          section="fileKeep"
          label="Days to keep files after a ticket closes"
          max={365}
          button="Save file keep time"
          saved={settings.fileKeep}
          notice={notices.fileKeep}
          setNotice={(n) => setNotice('fileKeep', n)}
          onSaved={applySaved('fileKeep')}
          onReload={() => load('fileKeep')}
        />
      ) : (
        <p className="why" role="note">This setting is not turned on yet.</p>
      )}

      <h2 className="adh" style={{ marginTop: 28 }}>Desk email</h2>
      <p className="adp">
        Who gets an email from each desk. They are emailed when a new ticket has one of the priorities
        picked below, when someone replies before anyone in IT has answered, and (AI Access only) about a
        Claude seat request. To add someone, type their name and pick them from the list. For a shared
        mailbox, type the full address and press Add.
      </p>
      {settings.emailDomains && settings.alertPriorities && Array.isArray(settings.deskEmail) && settings.deskEmail.length ? (
        <>
          <PrioritiesForm
            key={`prio-${settings.alertPriorities.version}`}
            known={settings.knownPriorities || []}
            saved={settings.alertPriorities}
            notice={notices.alertPriorities}
            setNotice={(n) => setNotice('alertPriorities', n)}
            onSaved={applySaved('alertPriorities')}
            onReload={() => load('alertPriorities')}
          />
          {settings.deskEmail.map((d) => (
            <DeskEmailForm
              key={`desk-${d.desk}-${d.version}`}
              label={(settings.desks || []).find((x) => x.id === d.desk)?.label || d.desk}
              saved={d}
              notice={notices[`deskEmail:${d.desk}`]}
              setNotice={(n) => setNotice(`deskEmail:${d.desk}`, n)}
              onSaved={applySaved(`deskEmail:${d.desk}`)}
              onReload={() => load(`deskEmail:${d.desk}`)}
            />
          ))}
          <DomainsForm
            key={`domains-${settings.emailDomains.version}`}
            saved={settings.emailDomains}
            notice={notices.emailDomains}
            setNotice={(n) => setNotice('emailDomains', n)}
            onSaved={applySaved('emailDomains')}
            onReload={() => load('emailDomains')}
          />
        </>
      ) : (
        <p className="why" role="note">This setting is not turned on yet.</p>
      )}
    </>
  );
}

/* A list shown as one chip per entry, each with its own remove button. Entries in `flagged` are shown
 * in red: they are kept but never used. */
function Chips({ items, flagged = [], what, onRemove, focusWhenEmpty }) {
  const ref = useRef(null);
  // The pressed button goes away, so move focus to the chip that took its place (or the one before);
  // with no chip left, to the add box.
  const remove = (item, i) => {
    onRemove(item);
    requestAnimationFrame(() => {
      const left = ref.current ? ref.current.querySelectorAll('.elist__x') : [];
      if (left.length) left[Math.min(i, left.length - 1)].focus();
      else if (focusWhenEmpty) focusWhenEmpty();
    });
  };
  if (!items.length) return null;
  return (
    <ul className="elist" aria-label={what} ref={ref}>
      {items.map((item, i) => (
        <li key={item} className={`chip elist__chip${flagged.includes(item) ? ' chip--error' : ''}`}>
          <span className="elist__text">{item}</span>
          <button type="button" className="elist__x" aria-label={`Remove ${item}`} onClick={() => remove(item, i)}>
            <span className="material-symbols-outlined" aria-hidden="true">close</span>
          </button>
        </li>
      ))}
    </ul>
  );
}

function Changed({ part }) {
  return part.updatedBy ? <p className="elist__hint">{lastChanged(part)}</p> : null;
}

// Text left in an add box is not part of the list, so Save waits for Add (or an empty box).
const PENDING = 'Press Add first, or clear the box - what is typed there is not in the list yet.';

const sameList = (a, b) => a.length === b.length && a.every((x, i) => x === b[i]);

/* The company's email domains. Only addresses on these get the desk email, so a typo or an outside
 * address is never mailed. One chip per domain; the server cleans and checks the list. */
function DomainsForm({ saved, notice, setNotice, onSaved, onReload }) {
  const [list, setList] = useState(saved.domains);
  const [text, setText] = useState('');
  const box = useRef(null);
  const titleId = useId();
  const save = useSectionSave({ section: 'emailDomains', version: saved.version, setNotice, onSaved, onReload });
  const pending = text.trim() !== '';
  const dirty = !sameList(list, saved.domains);
  const add = () => {
    const d = text.trim().toLowerCase().replace(/^@/, '');
    if (!d) return;
    setList((l) => (l.includes(d) ? l : [...l, d]));
    setText('');
    box.current?.focus(); // the Add button just turned off under the keyboard
  };
  return (
    <section className="card card--pad stack elist__card" aria-labelledby={titleId}>
      <h3 className="elist__title" id={titleId}>Allowed email domains</h3>
      <p className="elist__hint">
        The desk email only goes to addresses that end in one of these. This stops an alert going to an
        outside or mistyped address. Add a domain when IHS buys a new brand.
      </p>
      <Chips items={list} what="Allowed email domains" onRemove={(d) => setList((l) => l.filter((x) => x !== d))} focusWhenEmpty={() => box.current?.focus()} />
      {list.length === 0 ? <p className="elist__hint" role="note">None: no desk address can be saved, and nobody is emailed.</p> : null}
      <div className="inline" style={{ gap: 8 }}>
        <input
          ref={box}
          className="input"
          type="text"
          style={{ flex: '1 1 260px', minWidth: 0 }}
          aria-label="New email domain"
          placeholder="newbrand.com"
          maxLength={253}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
        />
        <button type="button" className="btn btn--quiet" disabled={!text.trim()} onClick={add}>Add</button>
      </div>
      <div className="inline" style={{ gap: 8 }}>
        <button type="button" className="btn btn--primary" disabled={!dirty || pending || save.busy} onClick={() => save.run({ domains: list.join('; ') })}>
          {save.busy ? 'Saving…' : 'Save allowed domains'}
        </button>
      </div>
      {pending ? <p className="elist__hint" role="note">{PENDING}</p> : null}
      <Notice notice={notice} onReload={() => { setNotice(null); onReload(); }} />
      <Changed part={saved} />
    </section>
  );
}

/* Which priorities alert the desk: the email, and the urgent alert on staff phones and desktops (api/src/shared/deskPush.js). The choices are the priorities that exist (the server's). */
function PrioritiesForm({ known, saved, notice, setNotice, onSaved, onReload }) {
  const [picked, setPicked] = useState(saved.priorities);
  const save = useSectionSave({ section: 'alertPriorities', version: saved.version, setNotice, onSaved, onReload });
  const dirty = [...picked].sort().join() !== [...saved.priorities].sort().join();
  const toggle = (p) => setPicked((list) => (list.includes(p) ? list.filter((x) => x !== p) : [...list, p]));
  const titleId = useId();
  return (
    <section className="card card--pad stack elist__card" aria-labelledby={titleId}>
      <h3 className="elist__title" id={titleId}>New tickets that alert their desk</h3>
      <p className="elist__hint">A new ticket with a picked priority emails its desk at once, and sends an urgent alert to the desk's staff who turned alerts on.</p>
      <div className="inline" style={{ flexWrap: 'wrap', gap: 6 }} role="group" aria-label="Alert priorities">
        {known.map((p) => (
          <button key={p} type="button" className={`o${picked.includes(p) ? ' o--on' : ''}`} aria-pressed={picked.includes(p)} onClick={() => toggle(p)}>
            {p}
          </button>
        ))}
      </div>
      {picked.length === 0 ? <p className="elist__hint" role="note">None picked: a new ticket emails nobody and sends no alert.</p> : null}
      <div className="inline" style={{ gap: 8 }}>
        <button type="button" className="btn btn--primary" disabled={!dirty || save.busy} onClick={() => save.run({ priorities: picked })}>
          {save.busy ? 'Saving…' : 'Save alert priorities'}
        </button>
      </div>
      <Notice notice={notice} onReload={() => { setNotice(null); onReload(); }} />
      <Changed part={saved} />
    </section>
  );
}

/* One desk's addresses. Add a person by searching the company directory, or type a full address (a
 * shared mailbox is not in the search). The server cleans and checks the list; its reason is shown
 * when it refuses. */
function DeskEmailForm({ label, saved, notice, setNotice, onSaved, onReload }) {
  const [list, setList] = useState(saved.addresses);
  const [pick, setPick] = useState('');
  // A new key empties the search box after an address is added.
  const [boxKey, setBoxKey] = useState(0);
  // Said when a directory person has no email address to add.
  const [noMail, setNoMail] = useState('');
  const addBox = useRef(null);
  const titleId = useId();
  const save = useSectionSave({ section: 'deskEmail', version: saved.version, setNotice, onSaved, onReload });
  const dirty = !sameList(list, saved.addresses);
  // Only an address waits for Add; a name being searched cannot be added, so it does not hold up Save.
  const pending = typeof pick === 'string' && pick.includes('@');
  const focusBox = () => requestAnimationFrame(() => addBox.current?.querySelector('input')?.focus());
  const emptyBox = () => {
    setPick('');
    setBoxKey((k) => k + 1);
    focusBox();
  };
  const add = (address) => {
    const a = String(address || '').trim().toLowerCase();
    if (!a) return;
    setList((l) => (l.includes(a) ? l : [...l, a]));
    setNoMail('');
    emptyBox();
  };
  // A person picked from the directory is added at once; plain typing waits for Add (or Enter).
  const onPick = (v) => {
    if (v && typeof v === 'object') {
      if (v.mail) {
        add(v.mail);
      } else {
        setNoMail(`${v.displayName || 'That person'} has no email address in the directory. Type their address instead.`);
        emptyBox();
      }
      return;
    }
    setNoMail('');
    setPick(v);
  };
  const typed = typeof pick === 'string' && pick.includes('@') ? pick : '';
  return (
    <section className="card card--pad stack elist__card" aria-labelledby={titleId}>
      <h3 className="elist__title" id={titleId}>{label}</h3>
      <Chips items={list} flagged={saved.unusable} what={`${label} email addresses`} onRemove={(a) => setList((l) => l.filter((x) => x !== a))} focusWhenEmpty={focusBox} />
      {list.length === 0 ? <p className="elist__hint" role="note">Nobody yet: this desk is emailed about nothing.</p> : null}
      {saved.unusable.length ? (
        <p className="elist__hint elist__hint--bad" role="note">In red: not on an allowed email domain, so never sent to. Remove it and save.</p>
      ) : null}
      <div className="inline" style={{ gap: 8, alignItems: 'flex-start' }} role="group" aria-label={`Add to ${label}`}>
        <div style={{ flex: '1 1 260px', minWidth: 0 }} ref={addBox}>
          <PersonPicker
            key={boxKey}
            value={pick}
            onChange={onPick}
            onEnter={() => add(typed)}
            label={`Add a person to ${label}`}
            placeholder="Type a name, or a full email address"
          />
        </div>
        <button type="button" className="btn btn--quiet" disabled={!typed} onClick={() => add(typed)}>Add</button>
      </div>
      {noMail ? <p className="elist__hint elist__hint--bad" role="alert">{noMail}</p> : null}
      <div className="inline" style={{ gap: 8 }}>
        <button
          type="button"
          className="btn btn--primary"
          disabled={(!dirty && !saved.unusable.length) || pending || save.busy}
          onClick={() => save.run({ desk: saved.desk, addresses: list.join('; ') })}
        >
          {save.busy ? 'Saving…' : `Save ${label} email`}
        </button>
      </div>
      {pending ? <p className="elist__hint" role="note">{PENDING}</p> : null}
      <Notice notice={notice} onReload={() => { setNotice(null); onReload(); }} />
      <Changed part={saved} />
    </section>
  );
}

/* One section = one save = one version. A STALE answer reloads the form from the server. */
function useSectionSave({ section, version, setNotice, onSaved, onReload }) {
  const [busy, setBusy] = useState(false);
  const run = async (values) => {
    setBusy(true);
    setNotice(null);
    try {
      const out = await saveAdminSettings(section, version, values);
      // The message is set BEFORE the settings change, so it is already there when the form is rebuilt.
      setNotice({ kind: 'saved', text: out.changed ? 'Saved.' : 'Nothing to change: these are already the saved settings.' });
      onSaved(out.settings);
    } catch (err) {
      // Not refused and not a walkthrough block: we cannot tell if it landed, so offer a reload to look.
      const unknown = !(err && (err.outcome === 'rejected' || err.code === 'WALKTHROUGH_WRITE_BLOCKED'));
      setNotice({ kind: 'error', text: wordsForSettingsError(err), reload: unknown });
      if (err && err.code === 'STALE') onReload();
    } finally {
      setBusy(false);
    }
  };
  return { run, busy };
}

function Notice({ notice, onReload }) {
  if (!notice) return null;
  if (notice.kind !== 'error') return <p className="why" role="status">{notice.text}</p>;
  return (
    <p className="why" role="alert">
      {notice.text}
      {notice.reload && <> <button type="button" className="btn btn--quiet" onClick={onReload}>Reload settings</button></>}
    </p>
  );
}

const formOf = (saved) => ({ startHour: saved.startHour, endHour: saved.endHour, workDays: saved.workDays, holidays: saved.holidays });

function WorkHoursForm({ saved, notice, setNotice, onSaved, onReload }) {
  const [form, setForm] = useState(formOf(saved));
  const [newDate, setNewDate] = useState('');
  const [dateProblem, setDateProblem] = useState(null);
  const save = useSectionSave({ section: 'calendar', version: saved.version, setNotice, onSaved, onReload });
  const dirty = calendarDirty(form, saved);

  const addDate = () => {
    const r = addHoliday(form.holidays, newDate);
    if (!r.ok) { setDateProblem(r.message); return; }
    setDateProblem(null);
    setNewDate('');
    setForm({ ...form, holidays: r.list });
  };

  return (
    <div className="stack" style={{ gap: 14 }}>
      <div className="fld">
        <label>
          <span className="field__label">Day starts</span>
          <select value={form.startHour} onChange={(e) => setForm({ ...form, startHour: Number(e.target.value) })}>
            {START_HOURS.map((h) => <option key={h} value={h}>{hourLabel(h)}</option>)}
          </select>
        </label>
        <label>
          <span className="field__label">Day ends</span>
          <select value={form.endHour} onChange={(e) => setForm({ ...form, endHour: Number(e.target.value) })}>
            {END_HOURS.map((h) => <option key={h} value={h}>{hourLabel(h)}</option>)}
          </select>
        </label>
      </div>

      <div>
        <span className="field__label" id="working-days-label">Working days</span>
        <div className="inline" role="group" aria-labelledby="working-days-label" style={{ flexWrap: 'wrap', gap: 6 }}>
          {WEEKDAYS.map((d) => (
            <button
              key={d.n}
              type="button"
              className={`o${form.workDays.includes(d.n) ? ' o--on' : ''}`}
              aria-pressed={form.workDays.includes(d.n)}
              onClick={() => setForm({ ...form, workDays: toggleDay(form.workDays, d.n) })}
            >
              {d.label}
            </button>
          ))}
        </div>
      </div>

      <div>
        <span className="field__label">Holidays (the desk is closed)</span>
        {form.holidays.length === 0 ? <p className="muted">No holidays listed.</p> : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {form.holidays.map((d) => (
              <li key={d} className="inline" style={{ justifyContent: 'space-between', maxWidth: 360 }}>
                <span>{holidayLabel(d)}</span>
                <button
                  type="button"
                  className="btn btn--ghost"
                  aria-label={`Remove ${holidayLabel(d)}`}
                  onClick={() => setForm({ ...form, holidays: removeHoliday(form.holidays, d) })}
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}
        <div className="inline" style={{ marginTop: 8, gap: 8 }}>
          <input type="date" value={newDate} aria-label="Add a holiday" onChange={(e) => { setNewDate(e.target.value); setDateProblem(null); }} />
          <button type="button" className="btn btn--quiet" disabled={!newDate} onClick={addDate}>Add holiday</button>
        </div>
        {dateProblem ? <p className="why" role="alert">{dateProblem}</p> : null}
      </div>

      <div className="inline" style={{ gap: 8 }}>
        <button type="button" className="btn btn--primary" disabled={!dirty || save.busy} onClick={() => save.run(form)}>
          {save.busy ? 'Saving…' : 'Save work hours'}
        </button>
        <button type="button" className="btn btn--ghost" disabled={!dirty || save.busy} onClick={() => setForm(formOf(saved))}>
          Undo changes
        </button>
      </div>
      <Notice notice={notice} onReload={() => { setNotice(null); onReload(); }} />
      <p className="why">{lastChanged(saved)}</p>
    </div>
  );
}

/* One whole-days setting: the reopen window, or how long a closed ticket keeps its files. */
function DaysForm({ section, label, max, button, saved, notice, setNotice, onSaved, onReload }) {
  const [days, setDays] = useState(saved.days);
  const save = useSectionSave({ section, version: saved.version, setNotice, onSaved, onReload });
  const dirty = days !== saved.days;
  return (
    <div className="stack" style={{ gap: 10 }}>
      <div className="fld">
        <label>
          <span className="field__label">{label}</span>
          <input type="number" min="1" max={max} step="1" value={days} onChange={(e) => setDays(Number(e.target.value))} />
        </label>
        <span>days</span>
      </div>
      {saved.storedValueUnusable ? (
        <p className="why" role="note">The stored value could not be used, so the built-in {saved.days} days is showing. Save a number to fix it.</p>
      ) : null}
      <div className="inline" style={{ gap: 8 }}>
        <button
          type="button"
          className="btn btn--primary"
          disabled={(!dirty && !saved.storedValueUnusable) || save.busy}
          onClick={() => save.run({ days })}
        >
          {save.busy ? 'Saving…' : button}
        </button>
      </div>
      <Notice notice={notice} onReload={() => { setNotice(null); onReload(); }} />
      <p className="why">{lastChanged(saved)}</p>
    </div>
  );
}
